import React, { useEffect, useState } from 'react'
import { PaymentElement, PaymentRequestButtonElement, useStripe, useElements } from '@stripe/react-stripe-js'
import { FRONT_URL, STRIPE_URL } from '../../config'
import { CircularProgress } from '@mui/material'
import { Box } from '@mui/system'
import { useTranslation } from 'react-i18next'
import { toast, ToastContainer, Slide } from 'react-toastify'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faCircleCheck,
  faCircleExclamation,
} from '@fortawesome/pro-solid-svg-icons'
import lightTheme from '../../styles/theme/lightTheme'

export default function CheckoutFormTest({
  paymentIntentId,
  clientSecret,
  userId,
  offreId,
  offrePrice,
  type,
  periode,
  onSuccess,
  onClose,
  codePromoObject,
  offreQuantity,
}) {
  const { t, i18n } = useTranslation()
  const stripe = useStripe()
  const elements = useElements()

  const [isLoading, setIsLoading] = useState(false)
  const [paymentRequest, setPaymentRequest] = useState(null)
  const [canMakePayment, setCanMakePayment] = useState(false)

  let user_id = userId //id utilisateur
  let offre_id = offreId //id offre
  let price = offrePrice // prix total a paye

  useEffect(() => {
    if (!stripe || !clientSecret || !price) {
      return
    }

    const amount = Math.round(Number(price) * 100)

    if (!Number.isFinite(amount) || amount <= 0) {
      console.error('Invalid Stripe amount:', price)
      return
    }

    const currency = sessionStorage.getItem('currency')
      ? JSON.parse(sessionStorage.getItem('currency')).devise.toLowerCase()
      : 'chf'


    const pr = stripe.paymentRequest({
      country: 'CH',
      currency: currency,
      total: {
        label: 'Jobertech Payment',
        amount,
      },
      requestPayerName: true,
      requestPayerEmail: true,
    })


    pr.canMakePayment()
      .then((result) => {

        if (result) {
          setPaymentRequest(pr)
          setCanMakePayment(true)
        } else {
          setPaymentRequest(null)
          setCanMakePayment(false)
        }
      })
      .catch((error) => {
        console.error(
          'Stripe canMakePayment error:',
          error
        )

        setPaymentRequest(null)
        setCanMakePayment(false)
      })

    const handlePaymentMethod = async (ev) => {
      setIsLoading(true)

      try {
        /*
         * IMPORTANT:
         *
         * For your old Stripe version, use
         * confirmCardPayment() here.
         */

        const result = await stripe.confirmCardPayment(
          clientSecret,
          {
            payment_method: ev.paymentMethod.id,
          },
          {
            handleActions: false,
          }
        )

        if (result.error) {
          console.error(
            'Stripe wallet payment error:',
            result.error
          )

          ev.complete('fail')

          toast.error(
            result.error.message ||
            t('successMessage.paymentNok')
          )

          setIsLoading(false)

          return
        }

        let paymentIntent = result.paymentIntent

        /*
         * 3DS / additional authentication
         */
        if (
          paymentIntent &&
          paymentIntent.status === 'requires_action'
        ) {
          const actionResult =
            await stripe.confirmCardPayment(
              clientSecret
            )

          if (actionResult.error) {
            ev.complete('fail')

            toast.error(
              actionResult.error.message ||
              t('successMessage.paymentNok')
            )

            setIsLoading(false)

            return
          }

          paymentIntent =
            actionResult.paymentIntent
        }

        /*
         * Payment succeeded
         */
        if (
          paymentIntent &&
          paymentIntent.status === 'succeeded'
        ) {
          ev.complete('success')

          await insertPayment(paymentIntent)

          return
        }

        /*
         * Payment is not completed
         */
        ev.complete('fail')

        setIsLoading(false)

      } catch (error) {
        console.error(
          'Wallet payment exception:',
          error
        )

        ev.complete('fail')

        toast.error(
          error.message ||
          t('successMessage.paymentNok')
        )

        setIsLoading(false)
      }
    }

    pr.on(
      'paymentmethod',
      handlePaymentMethod
    )

    return () => {
      pr.off(
        'paymentmethod',
        handlePaymentMethod
      )
    }
  }, [
    stripe,
    clientSecret,
    price,
  ])

  const insertPayment = async (paymentIntent) => {
    try {
      const response = await fetch(
        `${STRIPE_URL}payment_insert`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            request_type: 'payment_insert',
            payment_intent_id: paymentIntent.id,
            id_offre: offre_id,
            id_client: user_id,
            type: type,
            periode: periode,
            quantity: offreQuantity,
            prix: Number(price),
            langue: i18n.language,
            code_promo: codePromoObject?.id,
            devise: sessionStorage.getItem('currency')
              ? JSON.parse(
                sessionStorage.getItem('currency')
              ).devise
              : 'CHF',
          }),
        }
      )

      if (!response.ok) {
        throw new Error(
          'Unable to save payment'
        )
      }

      let data = await response.json()

      /*
       * Your API appears to return a JSON string.
       * Keep this only if that is really the API response.
       */
      if (typeof data === 'string') {
        data = JSON.parse(data)
      }

      if (
        data.payment_id &&
        data.payment_status === 'succeeded'
      ) {
        toast.success(
          t('successMessage.paymentOk')
        )

        setIsLoading(false)

        setTimeout(() => {
          onSuccess()
        }, 1000)

        return true
      }

      throw new Error(
        'Payment was not successfully saved'
      )

    } catch (error) {
      console.error(
        'payment_insert error:',
        error
      )

      toast.error(
        t('successMessage.paymentNok')
      )

      setIsLoading(false)

      setTimeout(() => {
        onClose()
      }, 1000)

      return false
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!stripe || !elements) {
      // Stripe.js has not yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      return
    }

    setIsLoading(true)

    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        // Url de la page de fin de paiement
        return_url: FRONT_URL,
      },
      redirect: 'if_required',
    })

    fetch(`${STRIPE_URL}payment_insert`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        request_type: 'payment_insert',
        payment_intent_id: paymentIntentId,
        id_offre: offre_id,
        id_client: user_id,
        type: type,
        periode: periode,
        quantity: offreQuantity,
        prix: parseInt(price),
        langue: i18n.language,
        code_promo: codePromoObject?.id,
        devise: sessionStorage.getItem('currency')
          ? JSON.parse(sessionStorage.getItem('currency')).devise
          : 'CHF',
      }),
    })
      .then((response) => response.json())
      .then((data) => {
        data = JSON.parse(data)
        if (data.payment_id && data.payment_status === 'succeeded') {
          toast.success(t('successMessage.paymentOk'), {
            position: 'top-right',
            autoClose: 5000,
            hideProgressBar: true,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: 'light',
            icon: ({ theme, type }) => (
              <FontAwesomeIcon
                icon={faCircleCheck}
                fontSize={25}
                color={lightTheme.palette.success.main}
              />
            ),
          })
          //setMessage(t('successMessage.paymentOk'))
          setIsLoading(false)
          setTimeout(() => {
            onSuccess()
          }, 1000)
        } else {
          toast.error(t('successMessage.paymentNok'), {
            position: 'top-right',
            autoClose: 5000,
            hideProgressBar: true,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: 'light',
            icon: ({ theme, type }) => (
              <FontAwesomeIcon
                icon={faCircleExclamation}
                fontSize={25}
                color={lightTheme.palette.error.main}
              />
            ),
          })
          //setMessage(t('successMessage.paymentNok'))
          setIsLoading(false)
          setTimeout(() => {
            onClose()
          }, 1000)
        }
      })
      .catch((error) => {
        setIsLoading(false)
      })

    // This point will only be reached if there is an immediate error when
    // confirming the payment. Otherwise, your customer will be redirected to
    // your `return_url`. For some payment methods like iDEAL, your customer will
    // be redirected to an intermediate site first to authorize the payment, then
    // redirected to the `return_url`.
    /* if (error.type === 'card_error' || error.type === 'validation_error') {
      setMessage(error.message)
    } else {
      setMessage('une erreur inattendue est apparue.')
    } */
  }

  return (
    <>
{/*       {canMakePayment && paymentRequest && (
        <PaymentRequestButtonElement
          options={{
            paymentRequest,
            style: {
              paymentRequestButton: {
                type: 'buy',
                theme: 'dark',
                height: '48px',
              },
            },
          }}
        />
      )} */}
      <form id="payment-form" onSubmit={handleSubmit}>
        <PaymentElement id="payment-element" />
        {isLoading ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <button disabled={isLoading || !stripe || !elements} id="submit">
            <span id="button-text">{t('button.pay')}</span>
          </button>
        )}
        {/* Afficher les messages d'erreur ou de réussite */}
        {/* {message && <div id="payment-message">{message}</div>} */}
      </form>
      {/*  <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        closeButton={false}
        transition={Slide}
        className={'toast'}
      /> */}
    </>
  )
}
